/**
 * Lemon Squeezy service — client-side helpers for checkout, premium status,
 * and subscription management.
 *
 * Unlike the previous RevenueCat integration, Lemon Squeezy does not have a
 * client-side SDK for purchases. The flow is:
 *   1. Build a checkout URL with user metadata (Firebase UID)
 *   2. User completes payment on Lemon Squeezy's hosted checkout
 *   3. Webhook writes subscription status to Firestore
 *   4. Client reads premium status from Firestore
 *
 * @example
 * import { getCheckoutUrl, fetchPremiumData } from '../services/lemonsqueezy';
 * const url = getCheckoutUrl('monthly', { uid: 'abc123', email: 'user@example.com' });
 * const premiumData = await fetchPremiumData('abc123');
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';

import { getFirebaseDb } from '../firebase';
import { LEMONSQUEEZY, FREEMIUM, FIRESTORE } from '../constants';

import type { PremiumData } from '../types';

// ---------------------------------------------------------------------------
// Plan types
// ---------------------------------------------------------------------------

export type PlanId = 'monthly';

export interface PlanInfo {
  id: PlanId;
  variantId: string;
  checkoutUrl: string;
}

/** Available plans (currently monthly only; yearly/lifetime to be added) */
export const PLANS: Record<PlanId, PlanInfo> = {
  monthly: {
    id: 'monthly',
    variantId: LEMONSQUEEZY.VARIANTS.MONTHLY,
    checkoutUrl: LEMONSQUEEZY.CHECKOUT_URLS.MONTHLY,
  },
} as const;

// ---------------------------------------------------------------------------
// Checkout URL builder
// ---------------------------------------------------------------------------

interface CheckoutOptions {
  /** Firebase UID — passed as custom data for webhook to associate payment */
  uid: string;
  /** User email for pre-filling the checkout form */
  email?: string;
  /** User display name for pre-filling */
  name?: string;
}

/**
 * Build a Lemon Squeezy checkout URL with pre-filled user data.
 *
 * Custom data (`checkout[custom][firebase_uid]`) allows the webhook to
 * associate the payment with the correct Firestore user document.
 *
 * @see https://docs.lemonsqueezy.com/help/checkout/prefilling-checkout-fields
 */
export function getCheckoutUrl(planId: PlanId, options: CheckoutOptions): string {
  const plan = PLANS[planId];
  if (!plan) throw new Error(`Unknown plan: ${planId}`);

  const url = new URL(plan.checkoutUrl);

  // Pre-fill user identity
  if (options.email) {
    url.searchParams.set('checkout[email]', options.email);
  }
  if (options.name) {
    url.searchParams.set('checkout[name]', options.name);
  }

  // Pass Firebase UID as custom data so the webhook can write to the correct user doc
  url.searchParams.set('checkout[custom][firebase_uid]', options.uid);

  return url.toString();
}

// ---------------------------------------------------------------------------
// Premium status from Firestore
// ---------------------------------------------------------------------------

/**
 * Fetch premium status from Firestore for the given Firebase UID.
 * Returns `null` if Firestore is unavailable or user doc doesn't exist.
 */
export async function fetchPremiumData(uid: string): Promise<PremiumData | null> {
  const db = getFirebaseDb();
  if (!db) return null;

  try {
    const userDoc = await getDoc(doc(db, FIRESTORE.USERS_COLLECTION, uid));
    if (!userDoc.exists()) return null;

    const data = userDoc.data();
    const premium = data?.[LEMONSQUEEZY.FIRESTORE_PREMIUM_FIELD] as PremiumData | undefined;

    if (!premium) return null;

    // Check if subscription has expired
    if (premium.expirationDate) {
      const expDate = new Date(premium.expirationDate);
      if (expDate < new Date()) {
        return { ...premium, isPremium: false };
      }
    }

    return premium;
  } catch (error) {
    console.error('[LemonSqueezy] fetchPremiumData failed:', error);
    return null;
  }
}

/**
 * Subscribe to real-time premium status changes from Firestore.
 * Calls `onUpdate` whenever the user's premium data changes.
 *
 * @returns Unsubscribe function.
 */
export function subscribeToPremiumStatus(
  uid: string,
  onUpdate: (data: PremiumData | null) => void,
): Unsubscribe {
  const db = getFirebaseDb();
  if (!db) {
    // Firestore unavailable — call with null immediately and return no-op
    onUpdate(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, FIRESTORE.USERS_COLLECTION, uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }

      const data = snapshot.data();
      const premium = data?.[LEMONSQUEEZY.FIRESTORE_PREMIUM_FIELD] as PremiumData | undefined;

      if (!premium) {
        onUpdate(null);
        return;
      }

      // Check expiration
      if (premium.expirationDate) {
        const expDate = new Date(premium.expirationDate);
        if (expDate < new Date()) {
          onUpdate({ ...premium, isPremium: false });
          return;
        }
      }

      onUpdate(premium);
    },
    (error) => {
      console.error('[LemonSqueezy] premium status listener error:', error);
      onUpdate(null);
    },
  );
}

// ---------------------------------------------------------------------------
// Customer portal
// ---------------------------------------------------------------------------

/**
 * Get the Lemon Squeezy customer portal URL for a subscription.
 *
 * The customer portal URL is stored in Firestore by the webhook handler
 * when a subscription is created. If not available, returns null.
 */
export async function getCustomerPortalUrl(uid: string): Promise<string | null> {
  const premiumData = await fetchPremiumData(uid);
  if (!premiumData?.lemonsqueezyCustomerId) return null;

  // Lemon Squeezy customer portal URL pattern
  return `https://milimrishonot.lemonsqueezy.com/billing`;
}

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

/**
 * Read cached premium status from localStorage.
 */
export function readCachedPremium(): PremiumData | null {
  try {
    const raw = localStorage.getItem(FREEMIUM.PREMIUM_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PremiumData;
  } catch (_error) {
    return null;
  }
}

/**
 * Write premium status to localStorage cache.
 */
export function writeCachedPremium(data: PremiumData): void {
  try {
    localStorage.setItem(FREEMIUM.PREMIUM_CACHE_KEY, JSON.stringify(data));
  } catch (_error) {
    // Silent fallback — localStorage might be full or unavailable
  }
}
